import {
  Box,
  FormControl,
  FormHelperText,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { FC, useEffect, useState, useRef } from 'react';
import { errorMessages } from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { EnumDropdownProps } from 'src/models/FieldProps.interface';
import { getIn } from 'formik';
import { ExpandMoreOutlined } from '@mui/icons-material';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';
import { getBasicMaterials } from 'src/services/material.service';
import { unitErrorTextStyles } from '../StyledComponents/StyledUnitPicker';

interface RawMaterial {
  id: string;
  name: string;
}

interface RawMaterialSelectProps extends EnumDropdownProps {
  rawMaterialsList?: RawMaterial[];
  fetchRawMaterials?: () => void;
}

const RawMaterialSelect: FC<RawMaterialSelectProps> = ({
  id,
  label,
  formikProps,
  sx,
  color = 'var(--primary-navy)',
  errorState = false,
  height = '40px',
  width = '450px',
  showLabel = true,
  selectedIds,
  rawMaterialsList,
  fetchRawMaterials,
}) => {
  const { showSnackbar } = StyledSnackbar();

  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (rawMaterialsList) {
      setRawMaterials(rawMaterialsList);
      return;
    }

    if (fetchRawMaterials) {
      if (!hasLoadedRef.current) {
        fetchRawMaterials();
        hasLoadedRef.current = true;
      }
      return;
    }

    if (!hasLoadedRef.current) {
      loadRawMaterials();
      hasLoadedRef.current = true;
    }
  }, [rawMaterialsList]);

  const loadRawMaterials = async () => {
    try {
      const materialData = await getBasicMaterials();
      setRawMaterials(materialData);
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  return (
    <Box mb={3}>
      {showLabel && (
        <Typography
          fontFamily="Outfit"
          fontWeight="400"
          fontSize="16px"
          color={color}
          mb={2}
        >
          {label}
        </Typography>
      )}
      <FormControl
        fullWidth
        error={
          (getIn(formikProps.touched, id) || formikProps.submitCount > 0) &&
          (Boolean(getIn(formikProps.errors, id)) || errorState)
        }
        sx={{ m: 0 }}
      >
        <Select
          id={id}
          name={id}
          value={getIn(formikProps.values, id)}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
          displayEmpty
          IconComponent={ExpandMoreOutlined}
          sx={{
            height: height,
            width: width,
            bgcolor: 'var(--text-bg)',
            border: 'none',
            borderRadius: 1,
            color: 'var(--primary-navy)',
            '& .MuiSelect-select': {
              padding: '10px 14px',
              fontFamily: 'Outfit',
              fontSize: '16px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--border)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-blue)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-blue)',
            },
            '& input': {
              fontFamily: 'Outfit',
              fontSize: '16px',
            },
            overflow: 'hidden',
            '& input:-webkit-autofill': {
              bgcolor: 'var(--bg-color)',
              WebkitBoxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-moz-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-ms-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            ...((formikProps.touched[id] || formikProps.submitCount > 0) &&
              Boolean(formikProps.errors[id]) && {
                borderColor: 'var(--error)',
              }),
            ...(errorState && {
              borderColor: 'var(--error)',
            }),
            ...sx,
          }}
          renderValue={(selected) => {
            const selectedMaterial = rawMaterials.find(
              (material) => material.id === selected
            );
            return selectedMaterial ? selectedMaterial.name : `Select ${label}`;
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: '230px',
                backgroundColor: 'var(--bg-color)',
                overflowY: 'auto',
              },
              sx: {
                mt: 0.5,
                borderRadius: 2,
                border: '1px solid var(--border)',
                bgcolor: 'var(--text-bg)',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'var(--bg-color)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'var(--loader-color)',
                  borderRadius: '4px',
                  height: '70px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: 'var(--primary-blue)',
                  cursor: 'pointer',
                },
              },
            },
          }}
        >
          {rawMaterials.map((material) => {
            const selectedValue = getIn(formikProps.values, id);

            const isDisabled =
              selectedIds?.includes(material.id) &&
              selectedValue !== material.id;

            return (
              <MenuItem
                key={material.id}
                value={material.id}
                disabled={isDisabled}
                sx={{
                  fontFamily: 'Outfit',
                  fontSize: '16px',
                  '&.Mui-selected': {
                    backgroundColor: 'var(--inactive-btn)',
                    color: 'var(--primary-blue)',
                  },
                }}
              >
                {material.name}
              </MenuItem>
            );
          })}
        </Select>

        {(getIn(formikProps.touched, id) || formikProps.submitCount > 0) &&
          getIn(formikProps.errors, id) && (
            <FormHelperText sx={{ ...unitErrorTextStyles }}>
              <ErrorIcon />
              {getIn(formikProps.errors, id)}
            </FormHelperText>
          )}
      </FormControl>
    </Box>
  );
};

export default RawMaterialSelect;
